<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        id="table-container"
        :class="[sizeControl,
                scrollColor,
                { 'table-header-fixed': isFixedList },
                { 'last-col-fixed': lastColFixed },
                { 'select-column-menu': selectColumnMenu },
				{ 'line-white': lineWhite }
        ]"
        class="ui-table"
        :style="{'max-height': maxHeight}"
        @scroll="scrollHandler">
        <div v-show="(loading || isLoading) && !customLoadArea">
            <ui-overlay-load />
        </div>

        <div v-show="(loading || isLoading) && customLoadArea" :class="loadingClass">
            <ui-load-indicator bg-color="#e7e9f4" color="#69D296" :h="50" :stroke="8" />
        </div>

		<ui-page-message
			v-if="data?.length == 0 && !loading"
			:icon="notFoundIcon"
			icon-size="60"
			:bg="true"
			:text="notFoundText" />

        <table v-else>
            <thead :ref="tableRef">
                <tr>
                    <th v-if="draggable">&nbsp;</th>
                    <th v-if="selectable">
                        <ui-checkbox
                            :id="idComputed"
                            :checked="allSelected"
                            design="xs"
                            @input="selectAll" />
                    </th>
                    <th
                        v-for="(column, index) in columns"
                        :key="index"
                        :width="column.size || 'auto'"
                        :class="column.class"
                        :data-index="snakeCase(column.title)"
                    >
                        <div class="th-infos">
                            <span v-html="column.title" />
                            <div v-if="column.sortable">
                                <i :class="checkSortClass(column)" />
                            </div>
                        </div>
                        <div
                            v-if="column.sortable && column.data"
                            :key="index"
                            class='click-sort'
                            @click="sort(column.data)"
                        />

                        <!-- Verifica se é a ultima coluna e tem a opção de mostrar o menu ativada -->
                        <template v-if="!disableRef && selectColumnMenu && index == columns.length - 1">
                            <ui-menu-card icon="fal fa-cog" >
                                <button
                                    v-for="(col, i) in columnsComputed"
                                    :key="i"
                                    :class="setActiveClass(snakeCase(col.title))"
                                    type="button"
                                    @click="setColumn(snakeCase(col.title))">
                                    {{ col.title }}
                                </button>
                            </ui-menu-card>
                        </template>
                        <template v-if="tableOptions && index == columns.length - 1" >
                            <slot name="options" />
                        </template>
                    </th>
                </tr>
            </thead>

            <template v-if="draggable">
                <vue-draggable
                    id="tbody"
                    :list="dataComputed"
                    class="body cell"
                    group="uid"
                    handle=".drag"
                    tag="tbody"
                    ghost-class="active"
                    @end="dragEndHandler" >

                    <component
                        :is="component"
                        v-for="(item, index) in dataComputed"
                        :key="uuid(item)"
                        :data="item"
                        :index="index"
                        :selectable="selectable"
                        :draggable="draggable"
                        :grad-drop-icon="gradDropIcon"
                        @selected="selected"
                        @reload="reloadHandler"
                        @update-line="updateLineHandler"
                        @hook:mounted="prepareTable(index)"
                        @hook:updated="prepareTable(index)" />
                </vue-draggable>
            </template>

            <template v-else>
                <tbody ref="tbody" class="body">
                    <component
                        :is="component"
                        v-for="(item, index) in data"
                        :key="uuid(item)"
                        :data="item"
                        :index="index"
                        :selectable="selectable"
                        @selected="selected"
                        @reload="reloadHandler"
                        @update-line="updateLineHandler"
                        @hook:mounted="prepareTable(index)"
                        @hook:updated="prepareTable(index)" />
                </tbody>
            </template>
        </table>
    </div>
</template>

<script>
	import gradDropIcon from '@/assets/imgs/drag-and-drop.svg'
	import { uiLoadIndicator, uiMenuCard, uiOverlayLoad } from '@/components/ui'
	import { uiCheckbox } from '@/components/ui/form'
	import vueDraggable from 'vuedraggable'
	import uiPageMessage from "@/components/ui/page-message"
	import { helpersMixin, tablesMixin } from '@/mixins'

	export default {
		name: 'ui-table',

		components: {
			uiOverlayLoad,
			uiLoadIndicator,
			uiMenuCard,
			uiCheckbox,
			vueDraggable,
			uiPageMessage
		},

		mixins: [ helpersMixin, tablesMixin ],

		props: {
			/**
			 * Usado apenas como identificador da tabela
			 * Normalmente usado para eventos e local-storage
			 */
			name: {
				type: String,
				required: true,
			},
			/**
			 * Define comportamento do componente
			 * @param {Array} data
			 * São os dados que montam a tabela
			 */
			data: {
				type: [Array, Object],
				required: true,
				default() {
					return []
				},
			},
			/**
			 * Altera layout do componente
			 * @param {String} trComponent
			 * Esta propriedade recebe o caminho relativo para o componente
			 * que será usado como linha na tabela.
			 * Sempre verificar a quantidade de colunas da linha com a prop columns
			 */
			trComponent: {
				type: String,
				required: false,
				default: ''
			},
			/**
			 * Altera layout do componente
			 * São os títulos das colunas <thead><tr><th>
			 */
			columns: {
				type: Array,
				required: false,
				default() {
					return []
				},
			},
			/**
			 * Altera layout do componente
			 * Define os espaços e tamanhos da celulas da tabela, assim como da paginação
			 */
			sizeControl: {
				type: String,
				default: 'sm',
				required: false,
			},
			/**
			 * Altera layout do componente
			 * Define a cor do scroll
			 */
            scrollColor: {
				type: String,
				default: 'scroll-color',
				required: false,
			},
			/**
			 * Altera layout e comportamento do componente
			 * Ícone que é exibido quando não se encontra itens para preencher a tabela
			 */
			notFoundIcon: {
				type: String,
				required: true,
			},
			/**
			 * Altera layout do componente
			 * Texto que é exibido quando não se encontra itens para preencher a tabela
			 */
			notFoundText: {
				type: String,
				required: true,
				default: ''
			},
			/**
			 * Altera layout do componente
			 * Mostra um <ui-overlay-load> sobre a tabela
			 */
			loading: {
				type: Boolean,
				required: true,
			},
			/**
			 * Altera layout e comportamento do componente
			 * Drag & Drop
			 * Permite ordenar/reordenar a tabela com a ação de arrastar
			 * É necessário adidionar uma coluna<td> no componente da linha para arrastar
			 */
			draggable: {
				type: Boolean,
				required: false,
				default: false,
			},
			/**
			 * Altera layout e comportamento do componente
			 * Permite selecionar uma ou mais linhas da tabela
			 * Adidiona uma nova coluna para selecionar todas as linhas
			 * É necessário adidionar uma coluna<td> no componente da linha com <checkbox>
			 */
			selectable: {
				type: Boolean,
				required: false,
				default: false,
			},
			/**
			 * Define a seleção de todas as linhas da tabela
			 */
			allSelected: {
				type: Boolean,
				required: false,
				default: false,
			},
			/**
			 * Define o layout do componente.
			 * Torna a ultima TD/coluna da tabela fixa afim de
			 * sempre mostrar o menu de acção da linha da tabela
			 */
			lastColFixed: {
				type: Boolean,
				required: false,
				default: true,
			},
			/**
			 * Define o comportamento do menu de acção da tabela.
			 * Neste menu é possível selecionar quais colunas mostrar ou ocultar
			 */
			selectColumnMenu: {
				type: Boolean,
				required: false,
				default: true,
			},
			/**
			 * Define o layout do componente.
			 * Mantem o header/topo da tabela fixo durante o scroll
			 */
			isFixedList: {
				type: Boolean,
				required: false,
				default: true,
			},
			/**
			 * Desativa a referencia da tabela onde não há o uso do compontente
			 * parada tarefas simples que não necessitam de toda lógica.
			 */
            disableRef: {
                type: Boolean,
                default: false,
            },
			/**
			 * Ativa classe que ativa loader personalizado.
			 * Adicionando: min-height, background
			 */
			customLoadArea: {
				type: Boolean,
                default: false,
			},
			/**
			 * Define se o botão de colunas será mostrado
			 */
            tableOptions: {
                type: Boolean,
                default: false,
            },
			/**
			 * Define a largura máxima da table
			 */
			maxHeight: {
				type: String,
				default: null
			},
			/**
			 * Define a coluna a ser utilizada para ordenar os dados
			 */
			sortBy: {
				type: Object,
				required: false,
				default() {
					return {
						value: 'id',
						dir: 'asc'
					}
				}
			},
			/**
			* caso queira as linhas da tabela apenas brancas
			* coloca true que desativa zebra striping
			* (linhas alternadas em cinza/branco)
			*/
			lineWhite: {
				type: Boolean,
				default: false
			}
		},

		data() {
			return {
				component: null,
				params: {
					sort_by: { ...this.sortBy }
				},
				isLoading: false,
			}
		},

		computed: {
			dataComputed() {
				return this.data.map((el) => {
					el.uid = Math.random().toString(16).slice(2)
					return el
				})
			},
			idComputed() {
				return Date.now().toString(36) + Math.random().toString(36).substr(2)
			},
			gradDropIcon() {
				return this.draggable ? `<div class="drag"><img class="drag-image" src="${gradDropIcon}" /></div>` : ''
			},
			/**
			 * Referência da table do componente.
			 */
            tableRef(){
                return this.disableRef ?  '' : 'thead'
            },

			/**
			 * Classes computadas da area de loading baseda nas props.
			 */
			loadingClass(){
				return {
					'loading-area': this.customLoadArea,
				}
			}
		},

		watch: {
			/**
			 * Observa por mudanças na prop das colunas da tabela de modo a alterar as colunas visiveis.
			 */
			columns(newData) {
				this.visibleColumns = newData.map((x) => {
					return this.snakeCase(x.title)
				})
			}
		},

		beforeMount() {
			/**
			 * Faz o carregamento do componente dinamico usado como linha da tabela
			 */
			this.loadComponent().then(() => {
				this.component = () => this.loadComponent()
			})
		},

		created() {
			this.getColumns()
		},

		mounted() {
			this.$refs.tbody.addEventListener('scroll', this.scrollHandler)
		},

		beforeDestroy() {
			this.$refs.tbody.removeEventListener('scroll', this.scrollHandler)
		},

		methods: {
			/** Emit o evento 'reload' */
			reloadHandler() {
				this.$emit('reload')
			},
			/**
			 * Método executado após uma linha emitir o evento de update
			 */
			updateLineHandler(event) {
				this.$set(this.data, event.index, event.value)
			},
			/**
			 * Método executado após fazer a seleção de todas linha da tabela
			 * Usado apenas quando this.allSelected = true
			 * Emite um evento 'select-all'
			 */
			selectAll(bool) {
				this.$emit('select-all', bool)
			},
			/**
			 * Método executado após fazer a seleção de uma linha da tabela
			 * Usado apenas quando this.selectable = true
			 * Emite um evento 'selected' com os dados do evento da linha
			 */
			selected(event) {
				this.$emit('selected', event)
			},
			/**
			 * @param Element
			 * Recebe um componente add uma propriedade uid
			 * Usado como identificador
			 */
			uuid(el) {
				if (el.uid) return el.uid
				this.$set(el, 'uid', Math.random().toString(16).slice(2))
				return el.uid
			},
			/**
			 * Método executado após o drag&drop
			 * Usado apenas quando this.draggable = true
			 * Emite um evento 'drag' com os seguintes dados:
			 * this.data atualizado, ou seja já com a nova ordem
			 * o antigo index do item ordenado
			 * o novo index do item ordenado
			 */
			dragEndHandler(event) {
				this.$emit('drag', {
					data: [...this.dataComputed],
					item: { ...this.dataComputed[event.newIndex] },
					newIndex: event.newIndex,
				})
			},
			scrollHandler(event) {
				const bottom =
					event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight

				if (bottom) {
					this.$emit('scroll')
				}
			},
            /**
             * Retorna a classe de ordenação correta para a coluna, de acordo com os parâmetros
             * setados para o DataTable
             */
            checkSortClass(column) {
                let classValue = 'fas fa-sort'

                if (column.data === this.params.sort_by.value
                    && this.params.sort_by.dir === 'asc') {
                    classValue = 'fas fa-sort-up'

                } else if (column.data === this.params.sort_by.value
                    && this.params.sort_by.dir === 'desc') {
                    classValue = 'fas fa-sort-down'
                }
                return classValue
            },
            /**
             * Realiza a ordenação da listagem de acordo com uma coluna configurada
             */
            sort(event) {
                if (this.params?.sort_by?.value === event) {
                    switch (this.params.sort_by.dir) {
                        case 'asc':
							this.params.sort_by.dir = 'desc'
                            break

                        default:
                            if (this.params.sort_by.value === this.sortBy.value) {
								this.params.sort_by.dir = 'asc'

                            } else {
								this.params.sort_by = { ...this.sortBy }
                            }
                            break
                    }

                } else {
					this.params.sort_by = { value: event, dir: 'asc' }
                }

				// Seta os dados de paginação no Vuex
				this.$store.dispatch('table/setSortBy', { ...this.params.sort_by })
				// Emite o evento para solicitar a ordenação
                this.$emit('sort')
            }
		},
	}
</script>

<style lang="stylus" scoped>
    @import 'styles.styl'
</style>
